/**
 * Condition Document Templates
 *
 * Types for the condition document library that maps Encompass condition
 * External IDs to reusable PDF templates with merge fields.
 */

/**
 * Condition timing - when the condition must be satisfied
 */
export type ConditionPriorTo = 'Docs' | 'Funding' | 'Closing';

/**
 * Condition category for grouping
 */
export type ConditionCategory =
  | 'App'
  | 'Credit'
  | 'Income'
  | 'Property'
  | 'Title'
  | 'NonQM'
  | 'Gov\'t'
  | 'Closing'
  | 'Other';

/**
 * A condition document template in the library
 */
export interface ConditionTemplate {
  id: string;
  organizationId: string | null;  // NULL = corporate template
  createdByUserId: string | null;

  // Condition mapping
  externalId: string;  // e.g., "0500"
  conditionTitle: string;
  conditionCategory: ConditionCategory | string | null;
  priorTo: ConditionPriorTo | null;

  // Template file info
  templateName: string;
  templateDescription: string | null;
  storagePath: string;
  storageBucket: string;
  originalFilename: string | null;
  fileSize: number | null;
  mimeType: string;

  // Merge field placeholders
  placeholders: string[];

  // Versioning
  version: number;
  isActive: boolean;
  isLocked: boolean;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Database row format (snake_case)
 */
export interface ConditionTemplateRow {
  id: string;
  organization_id: string | null;
  created_by_user_id: string | null;
  external_id: string;
  condition_title: string;
  condition_category: string | null;
  prior_to: string | null;
  template_name: string;
  template_description: string | null;
  storage_path: string;
  storage_bucket: string;
  original_filename: string | null;
  file_size: number | null;
  mime_type: string;
  placeholders: string[];
  version: number;
  is_active: boolean;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Request to create a new template
 */
export interface CreateConditionTemplateRequest {
  externalId: string;
  conditionTitle: string;
  conditionCategory?: ConditionCategory | string;
  priorTo?: ConditionPriorTo;
  templateName: string;
  templateDescription?: string;
  placeholders?: string[];
}

/**
 * Request to update a template
 */
export interface UpdateConditionTemplateRequest {
  conditionTitle?: string;
  conditionCategory?: ConditionCategory | string;
  priorTo?: ConditionPriorTo;
  templateName?: string;
  templateDescription?: string;
  placeholders?: string[];
  isActive?: boolean;
  isLocked?: boolean;
}

/**
 * Data available for merging into templates
 * Derived from ApprovalLetterData
 */
export interface TemplateMergeData {
  // Borrower
  borrower_name: string;
  borrower_first_name: string;
  borrower_last_name: string;
  borrower_email: string;
  borrower_phone: string;

  // Co-Borrower
  coborrower_name: string;
  coborrower_first_name: string;
  coborrower_last_name: string;

  // Property
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_full_address: string;
  property_type: string;

  // Loan
  loan_number: string;
  loan_amount: string;
  loan_purpose: string;
  loan_type: string;
  ltv: string;
  cltv: string;
  note_rate: string;
  term: string;

  // Lender
  lender_name: string;
  lender_contact: string;
  lender_phone: string;
  lender_fax: string;
  lender_address: string;

  // Team
  processor: string;
  underwriter: string;

  // Dates
  current_date: string;
  approval_date: string;
  lock_expiration: string;

  // Allow additional custom fields
  [key: string]: string;
}

/**
 * Simplified template info for UI display
 */
export interface ConditionTemplateInfo {
  externalId: string;
  templateName: string;
  conditionTitle: string;
}

/**
 * Convert database row to domain model
 */
export function rowToConditionTemplate(row: ConditionTemplateRow): ConditionTemplate {
  return {
    id: row.id,
    organizationId: row.organization_id,
    createdByUserId: row.created_by_user_id,
    externalId: row.external_id,
    conditionTitle: row.condition_title,
    conditionCategory: row.condition_category as ConditionCategory | null,
    priorTo: row.prior_to as ConditionPriorTo | null,
    templateName: row.template_name,
    templateDescription: row.template_description,
    storagePath: row.storage_path,
    storageBucket: row.storage_bucket,
    originalFilename: row.original_filename,
    fileSize: row.file_size,
    mimeType: row.mime_type,
    placeholders: row.placeholders || [],
    version: row.version,
    isActive: row.is_active,
    isLocked: row.is_locked,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/**
 * Standard merge field placeholders
 */
export const STANDARD_PLACEHOLDERS = [
  'borrower_name',
  'borrower_first_name',
  'borrower_last_name',
  'borrower_email',
  'borrower_phone',
  'coborrower_name',
  'coborrower_first_name',
  'coborrower_last_name',
  'property_address',
  'property_city',
  'property_state',
  'property_zip',
  'property_full_address',
  'property_type',
  'loan_number',
  'loan_amount',
  'loan_purpose',
  'loan_type',
  'ltv',
  'cltv',
  'note_rate',
  'term',
  'lender_name',
  'lender_contact',
  'lender_phone',
  'processor',
  'underwriter',
  'current_date',
  'approval_date',
  'lock_expiration'
] as const;

export type StandardPlaceholder = typeof STANDARD_PLACEHOLDERS[number];
